import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UserPicker from "../common/UserPicker";
import { layoutWorkflow, insertPoints, applyInstanceState } from "./wfLayout";

// The workflow designer: an auto-layout vertical-branching canvas.
//
// The graph is built by INSERTING between steps rather than dragging onto a canvas. There is no drag-and-drop
// and no connector-pulling, because positions are computed — dragging would imply the position means something
// when it does not, and pulling connectors is the single richest source of broken graphs in tools that allow
// it. What you can do instead is add a step between two steps, fork a decision, and delete. Every one of those
// leaves the graph connected by construction.
//
// The same component renders a RUNNING INSTANCE read-only (`readOnly` + `instance`), through the same layout
// function, so a case is always drawn by the code that drew the workflow it is executing.

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `n${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;

const NODE_ICON = {
  Start: "▶",
  Approval: "✓",
  Acknowledge: "👁",
  Decision: "◆",
  Join: "⇊",
  End: "■",
};

/** Node types a person may add. Start is created with the workflow; Join appears when a decision forks. */
const ADDABLE = ["Approval", "Acknowledge", "Decision", "End"];

export default function WorkflowDesigner({
  nodes = [],
  edges = [],
  onChange,
  readOnly = false,
  instance = null,
  tasks = [],
  validation = null,
  roles = [],
  // Directory access for the people pickers. Both are optional: without a token the picker degrades to the
  // plain typed field it replaced, which is exactly the behaviour a designer needs if auth is unreachable.
  directoryToken = null,
  assigneeSuggestions = null,
  selectedKey = null,
  onSelect,
}) {
  const { t } = useTranslation();
  const [internalSelected, setInternalSelected] = useState(null);
  const selected = selectedKey !== null ? selectedKey : internalSelected;
  const select = useCallback(
    (key) => (onSelect ? onSelect(key) : setInternalSelected(key)),
    [onSelect]
  );

  const layout = useMemo(() => layoutWorkflow(nodes, edges), [nodes, edges]);
  const inserts = useMemo(() => (readOnly ? [] : insertPoints(layout)), [layout, readOnly]);
  const states = useMemo(
    () => (instance ? applyInstanceState(layout, { activeNodeKeys: splitKeys(instance.activeNodeKeys), tasks }) : {}),
    [layout, instance, tasks]
  );

  // Validation errors keyed by node, so a problem is shown ON the node it belongs to rather than only in a
  // list the reader then has to correlate by hand.
  const errorsByNode = useMemo(() => {
    const map = new Map();
    (validation?.errors || []).forEach((e) => {
      const k = e.nodeKey || e.NodeKey;
      if (!k) return;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    });
    return map;
  }, [validation]);

  const graphErrors = (validation?.errors || []).filter((e) => !(e.nodeKey || e.NodeKey));

  // ── Mutations ───────────────────────────────────────────────────────────────────────────

  const emit = useCallback((nextNodes, nextEdges) => onChange && onChange(nextNodes, nextEdges), [onChange]);

  /** Inserts a step onto an existing connection: from → new → to. Connectivity is preserved by construction. */
  const insertOn = useCallback(
    (edge, nodeType) => {
      const key = uid();
      const from = edge.fromNodeKey ?? edge.FromNodeKey;
      const to = edge.toNodeKey ?? edge.ToNodeKey;

      const newNode = {
        nodeKey: key,
        nodeType,
        name: defaultName(t, nodeType),
        quorum: "Any",
        requireFourEyes: false,
        ...(nodeType === "End" ? { outcomeLabel: "Approved" } : {}),
      };

      let nextNodes = [...nodes, newNode];
      let nextEdges = edges.filter((e) => e !== edge);

      if (nodeType === "Decision") {
        // A decision needs somewhere for both answers to go, or it is born invalid and the person has to
        // discover that from an error. The original path becomes its default branch, and a second branch gets
        // its own ending — a shape that is complete the moment it appears.
        const rejectKey = uid();
        nextNodes = [
          ...nextNodes,
          { nodeKey: rejectKey, nodeType: "End", name: t("workflow.node.rejected", { defaultValue: "Rejected" }), outcomeLabel: "Rejected", quorum: "Any" },
        ];
        nextEdges = [
          ...nextEdges,
          { fromNodeKey: from, toNodeKey: key },
          { fromNodeKey: key, toNodeKey: rejectKey, matchType: "Field", pattern: "", targetField: "", branchLabel: t("workflow.branch.if", { defaultValue: "if…" }), order: 0, isElse: false },
          { fromNodeKey: key, toNodeKey: to, isElse: true, order: 1, branchLabel: t("workflow.branch.otherwise", { defaultValue: "otherwise" }) },
        ];
      } else if (nodeType === "Approval") {
        // An approval must route both outcomes; giving it only "approve" is the half-built shape the server
        // refuses at activation, so it is never created that way here.
        const rejectKey = uid();
        nextNodes = [
          ...nextNodes,
          { nodeKey: rejectKey, nodeType: "End", name: t("workflow.node.rejected", { defaultValue: "Rejected" }), outcomeLabel: "Rejected", quorum: "Any" },
        ];
        nextEdges = [
          ...nextEdges,
          { fromNodeKey: from, toNodeKey: key },
          { fromNodeKey: key, toNodeKey: to, matchType: "Decision", pattern: "Approve", order: 0, branchLabel: t("workflow.branch.approved", { defaultValue: "approved" }) },
          { fromNodeKey: key, toNodeKey: rejectKey, matchType: "Decision", pattern: "Reject", order: 1, branchLabel: t("workflow.branch.rejected", { defaultValue: "rejected" }) },
        ];
      } else {
        nextEdges = [...nextEdges, { fromNodeKey: from, toNodeKey: key }, { fromNodeKey: key, toNodeKey: to }];
      }

      emit(nextNodes, nextEdges);
      select(key);
    },
    [nodes, edges, emit, select, t]
  );

  /** Removes a step and heals the gap where that is unambiguous; refuses where it would silently drop a branch. */
  const removeNode = useCallback(
    (key) => {
      const node = nodes.find((n) => (n.nodeKey ?? n.NodeKey) === key);
      if (!node || node.nodeType === "Start") return;

      const incoming = edges.filter((e) => (e.toNodeKey ?? e.ToNodeKey) === key);
      const outgoing = edges.filter((e) => (e.fromNodeKey ?? e.FromNodeKey) === key);

      let nextEdges = edges.filter((e) => e !== undefined && ![...incoming, ...outgoing].includes(e));

      // Heal only the simple case. Reconnecting a fork by guesswork would quietly change what the workflow
      // does, which is worse than making the person rewire it deliberately.
      if (incoming.length === 1 && outgoing.length === 1) {
        nextEdges = [
          ...nextEdges,
          { fromNodeKey: incoming[0].fromNodeKey ?? incoming[0].FromNodeKey, toNodeKey: outgoing[0].toNodeKey ?? outgoing[0].ToNodeKey },
        ];
      }

      emit(nodes.filter((n) => (n.nodeKey ?? n.NodeKey) !== key), nextEdges);
      select(null);
    },
    [nodes, edges, emit, select]
  );

  const patchNode = useCallback(
    (key, patch) =>
      emit(
        nodes.map((n) => ((n.nodeKey ?? n.NodeKey) === key ? { ...n, ...patch } : n)),
        edges
      ),
    [nodes, edges, emit]
  );

  const patchEdge = useCallback(
    (edge, patch) => emit(nodes, edges.map((e) => (e === edge ? { ...e, ...patch } : e))),
    [nodes, edges, emit]
  );

  const selectedNode = nodes.find((n) => (n.nodeKey ?? n.NodeKey) === selected) || null;

  // ── Render ──────────────────────────────────────────────────────────────────────────────

  return (
    <div className="wf-designer">
      <div className="wf-canvas-wrap">
        <div className="wf-canvas" style={{ width: layout.width, height: layout.height }}>
          <svg className="wf-wires" width={layout.width} height={layout.height} aria-hidden="true">
            <defs>
              <marker id="wf-arrow" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
                <path d="M0,0 L9,4.5 L0,9 z" fill="#94a3b8" />
              </marker>
            </defs>
            {layout.connectors.map((c, i) => (
              <g key={i}>
                <path
                  d={c.path}
                  className={`wf-wire${c.isBack ? " wf-wire-back" : ""}`}
                  markerEnd="url(#wf-arrow)"
                />
                {(c.edge.branchLabel || c.edge.BranchLabel) && (
                  <text x={c.labelX} y={c.labelY} className="wf-wire-label" textAnchor="middle">
                    {c.edge.branchLabel ?? c.edge.BranchLabel}
                  </text>
                )}
              </g>
            ))}
          </svg>

          {Object.entries(layout.positions).map(([key, p]) => {
            const n = p.node;
            const type = n.nodeType ?? n.NodeType;
            const nodeErrors = errorsByNode.get(key) || [];
            const state = states[key];

            return (
              <div
                key={key}
                className={[
                  "wf-node",
                  `wf-node-${String(type).toLowerCase()}`,
                  selected === key ? "is-selected" : "",
                  nodeErrors.length ? "has-error" : "",
                  state ? `is-${state}` : "",
                ].join(" ").trim()}
                style={{ left: p.x, top: p.y, width: p.w, height: p.h }}
                onClick={() => select(key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && select(key)}
                title={nodeErrors.map((e) => e.message).join("\n") || undefined}
              >
                <span className="wf-node-icon">{NODE_ICON[type] || "•"}</span>
                <span className="wf-node-body">
                  <span className="wf-node-name">{n.name ?? n.Name ?? key}</span>
                  <span className="wf-node-meta">{describeNode(t, n, roles)}</span>
                </span>
                {nodeErrors.length > 0 && <span className="wf-node-warn" aria-label="problem">!</span>}
                {state && <span className={`wf-state-dot is-${state}`} />}
              </div>
            );
          })}

          {inserts.map((ins, i) => (
            <InsertButton key={i} x={ins.x} y={ins.y} onPick={(type) => insertOn(ins.edge, type)} t={t} />
          ))}
        </div>
      </div>

      <aside className="wf-props">
        {graphErrors.length > 0 && (
          <div className="wf-props-errors">
            <strong>{t("workflow.validation.title", { defaultValue: "Not ready to activate" })}</strong>
            <ul>
              {graphErrors.map((e, i) => (
                <li key={i}>{e.message}</li>
              ))}
            </ul>
          </div>
        )}

        {!selectedNode && (
          <div className="wf-props-empty">
            {readOnly
              ? t("workflow.props.readonlyHint", { defaultValue: "Select a step to see its details." })
              : t("workflow.props.hint", { defaultValue: "Select a step to edit it, or use + to add one between steps." })}
          </div>
        )}

        {selectedNode && (
          <NodeProperties
            node={selectedNode}
            edges={edges.filter((e) => (e.fromNodeKey ?? e.FromNodeKey) === selected)}
            errors={errorsByNode.get(selected) || []}
            roles={roles}
            directoryToken={directoryToken}
            assigneeSuggestions={assigneeSuggestions}
            readOnly={readOnly}
            onPatch={(patch) => patchNode(selected, patch)}
            onPatchEdge={patchEdge}
            onDelete={() => removeNode(selected)}
            t={t}
          />
        )}
      </aside>
    </div>
  );
}

/** The "+" between two steps — the only way to grow the graph, and it cannot leave anything disconnected. */
function InsertButton({ x, y, onPick, t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="wf-insert" style={{ left: x, top: y }}>
      <button
        type="button"
        className="wf-insert-btn"
        onClick={() => setOpen((v) => !v)}
        title={t("workflow.insert.title", { defaultValue: "Add a step here" })}
      >
        +
      </button>
      {open && (
        <div className="wf-insert-menu" onMouseLeave={() => setOpen(false)}>
          {ADDABLE.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                setOpen(false);
                onPick(type);
              }}
            >
              <span className="wf-insert-icon">{NODE_ICON[type]}</span>
              {t(`workflow.nodeType.${type.toLowerCase()}`, { defaultValue: type })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NodeProperties({
  node,
  edges,
  errors,
  roles,
  directoryToken,
  assigneeSuggestions,
  readOnly,
  onPatch,
  onPatchEdge,
  onDelete,
  t,
}) {
  const type = node.nodeType ?? node.NodeType;
  const raisesTask = type === "Approval" || type === "Acknowledge";
  const usesRole = !!node.assigneeRoleId;

  return (
    <div className="wf-props-body">
      <div className="wf-props-head">
        <span className={`wf-props-type wf-node-${String(type).toLowerCase()}`}>{NODE_ICON[type]}</span>
        <strong>{t(`workflow.nodeType.${String(type).toLowerCase()}`, { defaultValue: type })}</strong>
      </div>

      {errors.length > 0 && (
        <div className="wf-props-errors">
          <ul>
            {errors.map((e, i) => (
              <li key={i}>{e.message}</li>
            ))}
          </ul>
        </div>
      )}

      <label className="wf-field">
        <span>{t("workflow.props.name", { defaultValue: "Name" })}</span>
        <input
          value={node.name ?? node.Name ?? ""}
          disabled={readOnly}
          onChange={(e) => onPatch({ name: e.target.value })}
        />
      </label>

      {type === "End" && (
        <label className="wf-field">
          <span>{t("workflow.props.outcome", { defaultValue: "Outcome recorded" })}</span>
          <input
            value={node.outcomeLabel ?? node.OutcomeLabel ?? ""}
            disabled={readOnly}
            placeholder="Approved"
            onChange={(e) => onPatch({ outcomeLabel: e.target.value })}
          />
        </label>
      )}

      {raisesTask && (
        <>
          <div className="wf-field">
            <span>{t("workflow.props.assignTo", { defaultValue: "Assign to" })}</span>
            <div className="wf-seg">
              <button
                type="button"
                className={!usesRole ? "is-on" : ""}
                disabled={readOnly}
                onClick={() => onPatch({ assigneeRoleId: null })}
              >
                {t("workflow.props.aPerson", { defaultValue: "A person" })}
              </button>
              <button
                type="button"
                className={usesRole ? "is-on" : ""}
                disabled={readOnly}
                onClick={() => onPatch({ assigneeUserName: null, assigneeRoleId: roles[0]?.id ?? null })}
              >
                {t("workflow.props.aRole", { defaultValue: "A role" })}
              </button>
            </div>
          </div>

          {!usesRole ? (
            <div className="wf-field">
              <label className="wf-field-cap" htmlFor={`wf-assignee-${node.nodeKey ?? node.NodeKey ?? "node"}`}>
                {t("workflow.props.user", { defaultValue: "User name" })}
              </label>
              <UserPicker
                id={`wf-assignee-${node.nodeKey ?? node.NodeKey ?? "node"}`}
                value={node.assigneeUserName ?? node.AssigneeUserName ?? ""}
                disabled={readOnly}
                token={directoryToken}
                suggestions={assigneeSuggestions}
                placeholder={t("workflow.props.userPlaceholder", {
                  defaultValue: "Start typing a name, or browse",
                })}
                onChange={(v) => onPatch({ assigneeUserName: v, assigneeRoleId: null })}
              />
            </div>
          ) : (
            <>
              <label className="wf-field">
                <span>{t("workflow.props.role", { defaultValue: "Role" })}</span>
                <select
                  value={node.assigneeRoleId ?? ""}
                  disabled={readOnly}
                  onChange={(e) => onPatch({ assigneeRoleId: Number(e.target.value) || null, assigneeUserName: null })}
                >
                  <option value="">—</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.role ?? r.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="wf-field">
                <span>{t("workflow.props.quorum", { defaultValue: "How many must decide" })}</span>
                <select
                  value={node.quorum ?? node.Quorum ?? "Any"}
                  disabled={readOnly}
                  onChange={(e) => onPatch({ quorum: e.target.value })}
                >
                  <option value="Any">{t("workflow.quorum.any", { defaultValue: "Any one of them" })}</option>
                  <option value="All">{t("workflow.quorum.all", { defaultValue: "All of them" })}</option>
                  <option value="N">{t("workflow.quorum.n", { defaultValue: "A specific number" })}</option>
                </select>
              </label>

              {(node.quorum ?? node.Quorum) === "N" && (
                <label className="wf-field">
                  <span>{t("workflow.props.quorumCount", { defaultValue: "How many" })}</span>
                  <input
                    type="number"
                    min={1}
                    value={node.quorumCount ?? node.QuorumCount ?? ""}
                    disabled={readOnly}
                    onChange={(e) => onPatch({ quorumCount: Number(e.target.value) || null })}
                  />
                </label>
              )}
            </>
          )}

          <label className="wf-field">
            <span>{t("workflow.props.sla", { defaultValue: "Due after (hours)" })}</span>
            <input
              type="number"
              min={1}
              value={node.slaHours ?? node.SlaHours ?? ""}
              disabled={readOnly}
              placeholder={t("workflow.props.noSla", { defaultValue: "no deadline" })}
              onChange={(e) => onPatch({ slaHours: Number(e.target.value) || null })}
            />
          </label>

          {/* Escalation only ever fires off an SLA breach, so these appear only once there is one to breach.
              That keeps the validator's WF_ESCALATION_WITHOUT_SLA effectively unreachable from the designer
              rather than something a person discovers when activation is refused. */}
          {(node.slaHours ?? node.SlaHours) > 0 && (
            <>
              <div className="wf-field">
                <label className="wf-field-cap" htmlFor={`wf-escalate-${node.nodeKey ?? node.NodeKey ?? "node"}`}>
                  {t("workflow.props.escalateTo", { defaultValue: "Escalate to" })}
                </label>
                <UserPicker
                  id={`wf-escalate-${node.nodeKey ?? node.NodeKey ?? "node"}`}
                  value={node.escalateToUserName ?? node.EscalateToUserName ?? ""}
                  disabled={readOnly}
                  token={directoryToken}
                  suggestions={assigneeSuggestions}
                  placeholder={t("workflow.props.escalateToNone", { defaultValue: "whoever started the case" })}
                  onChange={(v) => onPatch({ escalateToUserName: v || null })}
                />
              </div>

              <label className="wf-field">
                <span>{t("workflow.props.reEscalation", { defaultValue: "Chase again every (hours)" })}</span>
                <input
                  type="number"
                  min={1}
                  value={node.reEscalationHours ?? node.ReEscalationHours ?? ""}
                  disabled={readOnly}
                  placeholder={t("workflow.props.reEscalationNone", { defaultValue: "escalate once only" })}
                  onChange={(e) => onPatch({ reEscalationHours: Number(e.target.value) || null })}
                />
              </label>
            </>
          )}

          <label className="wf-check">
            <input
              type="checkbox"
              checked={!!(node.requireFourEyes ?? node.RequireFourEyes)}
              disabled={readOnly}
              onChange={(e) => onPatch({ requireFourEyes: e.target.checked })}
            />
            <span>
              {t("workflow.props.fourEyes", { defaultValue: "Require a second pair of eyes" })}
              <em>
                {t("workflow.props.fourEyesHint", {
                  defaultValue: "The decider must differ from whoever started this and from the previous approver.",
                })}
              </em>
            </span>
          </label>
        </>
      )}

      {type === "Decision" && edges.length > 0 && (
        <div className="wf-branches">
          <span className="wf-branches-title">{t("workflow.props.branches", { defaultValue: "Branches" })}</span>
          {edges.map((e, i) => (
            <div key={i} className="wf-branch">
              <input
                className="wf-branch-label"
                value={e.branchLabel ?? e.BranchLabel ?? ""}
                disabled={readOnly}
                placeholder={t("workflow.props.branchLabel", { defaultValue: "label" })}
                onChange={(ev) => onPatchEdge(e, { branchLabel: ev.target.value })}
              />
              {e.isElse || e.IsElse ? (
                <span className="wf-branch-else">
                  {t("workflow.props.otherwise", { defaultValue: "otherwise (default)" })}
                </span>
              ) : (
                <div className="wf-branch-cond">
                  <select
                    value={e.matchType ?? e.MatchType ?? "Field"}
                    disabled={readOnly}
                    onChange={(ev) => onPatchEdge(e, { matchType: ev.target.value })}
                  >
                    <option value="Field">{t("workflow.match.field", { defaultValue: "field equals" })}</option>
                    <option value="Keyword">{t("workflow.match.keyword", { defaultValue: "contains" })}</option>
                    <option value="Regex">{t("workflow.match.regex", { defaultValue: "matches pattern" })}</option>
                    <option value="Luhn">{t("workflow.match.luhn", { defaultValue: "looks like a card number" })}</option>
                  </select>
                  <input
                    value={e.targetField ?? e.TargetField ?? ""}
                    disabled={readOnly}
                    placeholder={t("workflow.props.field", { defaultValue: "field" })}
                    onChange={(ev) => onPatchEdge(e, { targetField: ev.target.value })}
                  />
                  <input
                    value={e.pattern ?? e.Pattern ?? ""}
                    disabled={readOnly}
                    placeholder={t("workflow.props.value", { defaultValue: "value" })}
                    onChange={(ev) => onPatchEdge(e, { pattern: ev.target.value })}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && type !== "Start" && (
        <button type="button" className="wf-delete" onClick={onDelete}>
          {t("workflow.props.delete", { defaultValue: "Remove this step" })}
        </button>
      )}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────────────────

const splitKeys = (csv) => (csv ? String(csv).split(",").map((s) => s.trim()).filter(Boolean) : []);

function defaultName(t, nodeType) {
  switch (nodeType) {
    case "Approval": return t("workflow.node.newApproval", { defaultValue: "Approval" });
    case "Acknowledge": return t("workflow.node.newAcknowledge", { defaultValue: "Acknowledge" });
    case "Decision": return t("workflow.node.newDecision", { defaultValue: "Decision" });
    case "End": return t("workflow.node.newEnd", { defaultValue: "End" });
    default: return nodeType;
  }
}

/** The one line under a node's name — what a reader most needs to know without opening the panel. */
function describeNode(t, n, roles) {
  const type = n.nodeType ?? n.NodeType;
  if (type === "End") return n.outcomeLabel ?? n.OutcomeLabel ?? "";
  if (type === "Decision") return t("workflow.node.decisionHint", { defaultValue: "routes on a condition" });
  if (type === "Join") return t("workflow.node.joinHint", { defaultValue: "waits for every branch" });
  if (type === "Start") return t("workflow.node.startHint", { defaultValue: "entry point" });

  const bits = [];
  const roleId = n.assigneeRoleId ?? n.AssigneeRoleId;
  if (roleId) {
    const role = roles.find((r) => r.id === roleId);
    bits.push(role ? (role.role ?? role.name) : `role #${roleId}`);
    const q = n.quorum ?? n.Quorum;
    if (q === "All") bits.push(t("workflow.quorum.allShort", { defaultValue: "all must approve" }));
    if (q === "N") bits.push(t("workflow.quorum.nShort", { defaultValue: "{{n}} must approve", n: n.quorumCount ?? n.QuorumCount ?? "?" }));
  } else if (n.assigneeUserName ?? n.AssigneeUserName) {
    bits.push(n.assigneeUserName ?? n.AssigneeUserName);
  } else {
    bits.push(t("workflow.node.noAssignee", { defaultValue: "nobody assigned" }));
  }

  const sla = n.slaHours ?? n.SlaHours;
  if (sla) bits.push(t("workflow.node.dueIn", { defaultValue: "due in {{h}}h", h: sla }));

  const escalateTo = n.escalateToUserName ?? n.EscalateToUserName;
  if (sla && escalateTo) bits.push(t("workflow.node.escalatesTo", { defaultValue: "escalates to {{who}}", who: escalateTo }));

  const repeat = n.reEscalationHours ?? n.ReEscalationHours;
  if (sla && repeat) bits.push(t("workflow.node.repeatsEvery", { defaultValue: "repeats every {{h}}h", h: repeat }));
  if (n.requireFourEyes ?? n.RequireFourEyes) bits.push(t("workflow.node.fourEyesShort", { defaultValue: "4-eyes" }));

  return bits.join(" · ");
}
